<script lang="ts" setup>
    import type { PageSettingsStoryblok } from '~/.storyblok/types/PageSettingsStoryblok';
    import type { StoryblokRichTextDoc } from '~/.storyblok/types/storyblok';
    import { buildLocalizedPath } from '~/utils/buildLocalizedPath';

    const props = defineProps<{
        blok: PageSettingsStoryblok;
        denied: boolean;
    }>();

    const emit = defineEmits<{
        confirm: [];
        deny: [];
    }>();

    function hasTextContent(node: StoryblokRichTextDoc | StoryblokRichTextDoc['content'][number]): boolean {
        if ('text' in node && node.text.trim()) return true;
        return 'content' in node && Boolean(node.content?.some(hasTextContent));
    }

    const { lang } = useLanguage();
    const globalHomeUrl = computed(() => buildLocalizedPath('global', lang.value));
    const hasCustomText = computed(() => Boolean(props.blok.gateText && hasTextContent(props.blok.gateText)));
    const deniedMessage = ref<HTMLParagraphElement>();

    watch(
        () => props.denied,
        (denied) => {
            if (denied) deniedMessage.value?.focus();
        },
        { flush: 'post' }
    );

    function handleDeny() {
        emit('deny');
    }

    function handleConfirm() {
        emit('confirm');
    }
</script>

<template>
    <SharedPageContent>
        <div
            v-editable="props.blok"
            class="gating-container"
        >
            <div class="text">
                <p
                    v-if="denied"
                    ref="deniedMessage"
                    tabindex="-1"
                >
                    {{ $t('confirmationGate.deniedMessage') }}
                </p>
                <RichText
                    v-else-if="hasCustomText"
                    :doc="blok.gateText"
                />
                <template v-else>
                    <p>
                        <strong>{{ $t('confirmationGate.eyebrow') }}</strong>
                    </p>
                    <h1>{{ $t('confirmationGate.headline') }}</h1>
                    <p class="default-text">{{ $t('confirmationGate.text') }}</p>
                </template>
            </div>

            <div class="button-container">
                <PrimaryButton
                    v-if="denied"
                    :button-text="$t('general.toHome')"
                    :button-href="globalHomeUrl"
                    size="small"
                    has-icon
                    space-between
                />
                <template v-else>
                    <PrimaryButton
                        :button-text="$t('confirmationGate.confirmLabel')"
                        size="small"
                        native-button
                        has-icon
                        space-between
                        @click="handleConfirm"
                    />
                    <PrimaryButton
                        :button-text="$t('confirmationGate.denyLabel')"
                        variant="secondary"
                        size="small"
                        native-button
                        has-icon
                        space-between
                        @click="handleDeny"
                    />
                </template>
            </div>
        </div>
    </SharedPageContent>
</template>

<style lang="scss" scoped>
    .gating-container {
        min-height: 60vh;
        display: flex;
        flex-direction: column;
        justify-content: center;

        .text {
            margin-bottom: var(--spacing--2);
            max-width: 80ch;

            :deep(p:not(:last-child)) {
                margin-bottom: var(--spacing);
            }

            .default-text {
                white-space: pre-line;
            }

            :deep(h1) {
                font-size: var(--font-xl-mobile);
                font-weight: 700;
                line-height: 1.25;
                margin-bottom: var(--spacing);

                @media (min-width: $br-m) {
                    font-size: var(--font-l);
                }

                @media (min-width: $br-l) {
                    font-size: var(--font-xl);
                }
            }
        }

        .button-container {
            display: flex;
            flex-direction: column;
            gap: var(--spacing);

            @media (min-width: $br-s) {
                flex-direction: row;
            }
        }
    }
</style>
